import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useDebouncedCallback } from 'use-debounce';
import { Checkbox, Grid, IconButton, Input, Paper, SelectChangeEvent, Stack } from '@mui/material';
import {
  Cancel as CancelIcon,
  Done as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Add as CreateIcon,
} from '@mui/icons-material';

import { Table } from '../../components/table/table';
import { Filter } from '../../components/filter/filter';
import { FilterField } from '../../components/filter/types';
import { HeadCell, Row, RowCell } from '../../components/table/types';

import {
  PostFeeLevelsDTO,
  UpdateCountryDTO,
  useDeleteFeeLevelsMutation,
  useGetCountriesQuery,
  useGetFeeLevelsQuery,
  usePostFeeLevelsMutation,
  usePutFeeLevelsMutation,
  useUpdateCountryMutation,
} from '../../api';
import type { Country, FeeLevel } from '../../types';
import { permissionsCheck } from '../../utils/permission';
import { useParams } from 'react-router-dom';
import { UTCToDateTime } from '../../utils';
import { layoutAction } from '../../store/sideBarStore/layoutStore';
import { useAppActions } from '../../utils/hooks';
import { CreateFeeLevelModal } from './components/CreateFeeLevelModal';
import { DeleteFeeLevelModal } from './components/DeleteFeeLevelModal';

export const FeeLevels: FC = () => {
  const { t } = useTranslation();
  const { fee_group_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToEditFeeLevels = permissionsCheck(['WRITE_FEE_LEVELS']);

  // api
  const {
    data: feeLevels,
    isLoading: isFeeLevelsLoading,
    refetch,
  } = useGetFeeLevelsQuery({ fee_group_id: Number(fee_group_id) });
  const [updateFeeLevel, { isLoading: isUpdateFeeLevelLoading }] = usePutFeeLevelsMutation();
  const [createFeeLevel, { isLoading: isCreateFeeLevelLoading }] = usePostFeeLevelsMutation();
  const [deleteFeeLevel, { isLoading: isDeleteFeeLevelLoading }] = useDeleteFeeLevelsMutation();

  // variables
  const isLoading = isFeeLevelsLoading || isUpdateFeeLevelLoading;
  const [actibeFeeLevel, setActibeFeeLevel] = useState<FeeLevel | null>();
  const [editFields, setEditFields] = useState<PostFeeLevelsDTO | null>(null);
  const handleEditFields = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    // @ts-ignore
    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };
  // add fee modal logic
  const [createModalShown, setCreateModalShown] = useState(false);
  const openCreateModal = () => {
    setCreateModalShown(true);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
    setActibeFeeLevel(null);
  };

  // delete fee modal logic
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const openDeleteModal = (fee_level: FeeLevel) => {
    setDeleteModalShown(true);
    setActibeFeeLevel(fee_level);
  };
  const closeDeleteModal = () => {
    setDeleteModalShown(false);
    setActibeFeeLevel(null);
  };

  // edit mode logic
  const [isEditMode, setEditMode] = useState(false);
  const startEditMode = (fee_level: FeeLevel) => {
    setActibeFeeLevel(fee_level);
    setEditMode(true);
  };
  const offEditMode = () => {
    setActibeFeeLevel(null);
    setEditMode(false);
  };

  // show action button
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    hasPermissionToEditFeeLevels &&
      setSpeedDialButtonComponent([
        { icon: <CreateIcon />, onClick: openCreateModal, disabled: isEditMode },
      ]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, [isEditMode]);

  // edit handler
  const editFeeLevel = async () => {
    if (!actibeFeeLevel) return;
    const arg = {
      fee_level_id: actibeFeeLevel?.id,
      body: {
        fee_group_id: Number(fee_group_id),
        min_turnover: actibeFeeLevel?.min_turnover,
      },
    };
    try {
      await updateFeeLevel(arg).unwrap();
      offEditMode();
      closeCreateModal();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // create handler
  const createFeeLevelHanler = async () => {
    if (!editFields) return;
    const arg = {
      fee_group_id: Number(fee_group_id),
      min_turnover: Number(editFields?.min_turnover),
    };
    try {
      await createFeeLevel(arg).unwrap();
      offEditMode();
      closeCreateModal();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // delete handler
  const deleteFeeLevelHanler = async () => {
    if (!actibeFeeLevel) return;
    const arg = {
      fee_group_id: Number(fee_group_id),
      fee_level_id: Number(actibeFeeLevel?.id),
    };
    try {
      await deleteFeeLevel(arg).unwrap();
      offEditMode();
      closeDeleteModal();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('fee-levels.head-cells.min-turnover') },
    { id: '2', label: t('fee-levels.head-cells.row-start') },
    {
      id: '3',
      label: t('fee-levels.head-cells.actions'),
      hidden: !hasPermissionToEditFeeLevels,
    },
  ];
  const sortedFeeLevels = useMemo(() => {
    if (!feeLevels?.feeLevels) return [];
    return [...feeLevels.feeLevels].sort((a, b) => a.min_turnover - b.min_turnover);
  }, [feeLevels?.feeLevels]);

  const rows: Row[] | undefined = sortedFeeLevels.map((it) => {
    const { min_turnover, row_start, id } = it;

    const cells: RowCell[] = [
      {
        name: 'min_turnover',
        value: min_turnover,
        component: isEditMode ? (
          actibeFeeLevel?.id === id ? (
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Input
                name="min_turnover"
                defaultValue={actibeFeeLevel?.min_turnover}
                onChange={(e) =>
                  setActibeFeeLevel({ ...actibeFeeLevel, min_turnover: Number(e.target.value) })
                }
                fullWidth
                disabled={isLoading}
              />
            </Stack>
          ) : undefined
        ) : (
          <>{min_turnover}</>
        ),
      },
      { name: 'row_start', value: UTCToDateTime(row_start) },
      {
        name: 'actions',
        hidden: !hasPermissionToEditFeeLevels,
        component: isEditMode ? (
          actibeFeeLevel?.id === id ? (
            <Stack direction="row" spacing={2}>
              <IconButton
                aria-label="save"
                color="success"
                onClick={() => editFeeLevel()}
                disabled={isUpdateFeeLevelLoading}
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                aria-label="cancel"
                color="error"
                onClick={() => offEditMode()}
                disabled={isUpdateFeeLevelLoading}
              >
                <CancelIcon />
              </IconButton>
            </Stack>
          ) : undefined
        ) : (
          <Stack direction="row" spacing={2}>
            <IconButton
              aria-label="edit"
              color="warning"
              onClick={() => startEditMode(it)}
              disabled={isLoading}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              aria-label="delete"
              color="error"
              onClick={() => openDeleteModal(it)}
              disabled={isLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ];

    return { cells };
  });

  return (
    <Stack>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <CreateFeeLevelModal
        open={createModalShown}
        onClose={closeCreateModal}
        onCreate={createFeeLevelHanler}
        fields={editFields}
        fieldsHandler={handleEditFields}
        isLoading={isCreateFeeLevelLoading}
      />

      <DeleteFeeLevelModal
        open={deleteModalShown}
        onClose={closeDeleteModal}
        onDelete={deleteFeeLevelHanler}
        isLoading={isDeleteFeeLevelLoading}
      />
    </Stack>
  );
};
